import { router } from 'expo-router';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useDeleteProgram } from '@/api/hooks/programHooks';
import { Program } from '@/api/models/programModels';
import { formatCurrency } from '@/utils/currencyFormat';
import { formatDayTime } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';

import DeleteButton from '../buttons/DeleteButton';
import EditButton from '../buttons/EditButton';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface ProgramCardProps {
  canEdit: boolean;
  program: Program;
  onCardPress?: () => void;
  onDelete?: () => void;
}

const ProgramCard = ({ canEdit, program, onCardPress, onDelete }: ProgramCardProps) => {
  const { mutateAsync: deleteProgram, isPending } = useDeleteProgram();

  const [showPicker, setShowPicker] = useState(false);

  const onProgramDeleted = async () => {
    try {
      deleteProgram(
        {
          id: program.id
        },
        {
          onSuccess: () => {
            setShowPicker(false);
            onDelete && onDelete();
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <TouchableOpacity onPress={onCardPress} disabled={onCardPress === undefined}>
      <View style={style.mainContainer}>
        <View style={style.header}>
          <View style={style.headerTitle}>
            <Text fontWeight="700" style={style.title} numberOfLines={2}>
              {program.name}
            </Text>
          </View>
          {canEdit && (
            <View style={style.buttonsContainer}>
              <DeleteButton onPress={() => setShowPicker(true)} />
              <EditButton
                onPress={() =>
                  router.navigate({
                    pathname: '/edit/editOneProgram',
                    params: {
                      programDataJson: JSON.stringify(program)
                    }
                  })
                }
              />
            </View>
          )}
        </View>
        {program.programDate && <Text style={style.normalText}>{formatDayTime(program.programDate)}</Text>}
        <Text style={style.normalText}>{program.description}</Text>
        <Text style={style.normalText}>
          {`${i18n.t('program_detail.annual_budget')}: `}
          <Text fontWeight="700" style={style.normalText}>
            {program.budget ? formatCurrency(program.budget) : i18n.t('mix.n_a')}
          </Text>
        </Text>
      </View>
      <ConfirmWarningModal
        showPicker={showPicker}
        loading={isPending}
        onClosePicker={() => setShowPicker(false)}
        onConfirmTapped={onProgramDeleted}
      />
    </TouchableOpacity>
  );
};

export default ProgramCard;

const style = StyleSheet.create({
  mainContainer: {
    flexDirection: 'column',
    gap: 10
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  headerTitle: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },

  image: {
    width: 28,
    height: 28
  },

  title: {
    flex: 1,
    flexWrap: 'wrap',
    color: 'black',
    fontSize: 22
  },

  normalText: {
    fontSize: 14
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 10
  }
});
